import { QueryClient } from '@tanstack/react-query';

import { createApiClient, type ApiClient, type ApiError } from '@platform/api-client';
import { ANONYMOUS_SESSION, SESSION_QUERY_KEY } from '@platform/api-client/react';

/**
 * The clinic admin app's one API client and query cache (MASTER_PLAN M3.9, M2.10).
 *
 * Both live here rather than in `main.tsx` because they refer to each other: a `401`
 * from any endpoint has to put the session cache back to "anonymous", and the session
 * cache is what the shell, the nav, the banner and every guard read (M2.7). Same shape as
 * `apps/patient/src/lib/api/client.ts` and `apps/super/src/lib/api/client.ts`, for the
 * same reason.
 *
 * ## No persister, ever
 *
 * TanStack Query's cache stays in memory only. Anything written to `localStorage` or
 * IndexedDB would put PHI in the browser's storage (ADR-007, SECURITY.md §7).
 *
 * ## Step-up (M2.7, additive)
 *
 * `createApiClient` offers an `onStepUpRequired` callback and it is wired below, to the
 * one-listener bridge `lib/session/step-up.tsx` subscribes to. Neither callback
 * navigates and neither opens anything itself: the router does not exist when this
 * module is evaluated, and two things that can move the user is one too many.
 * `onUnauthenticated` records the fact and `RequireAuth` - which knows the route, and
 * therefore what `?next=` should be - does the navigating.
 *
 * The integrations screens still catch `403 MFA_STEP_UP_REQUIRED` where the call was
 * made (`features/integrations/step-up-prompt.tsx`, itself a `TODO(M2.7)`), because that
 * dialog re-runs *that* mutation. Until that seam adopts `useStepUp().guard`, the root
 * layout passes `notifyUnguarded={false}` so the two prompts cannot both open for one
 * refusal - see `Docs/ADMIN_AUTH_UI.md` §6.
 */

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

/* ------------------------------------------------------------------ *
 * The step-up bridge (M2.7)
 * ------------------------------------------------------------------ */

type StepUpListener = (error: ApiError) => void;

let stepUpListener: StepUpListener | null = null;

/**
 * Subscribes to `403 MFA_STEP_UP_REQUIRED` from anywhere.
 *
 * One listener, not a set: there is exactly one step-up dialog in the app, and a second
 * subscriber would mean two dialogs racing for the same verification. The provider
 * installs itself on mount and removes itself on unmount.
 */
export function subscribeToStepUp(listener: StepUpListener): () => void {
  stepUpListener = listener;
  return () => {
    if (stepUpListener === listener) stepUpListener = null;
  };
}

export const apiClient: ApiClient = createApiClient({
  /*
   * Same origin. In development Vite proxies `/api` to the API with
   * `changeOrigin: false`, so the clinic host the browser used - and therefore the
   * tenant - survives the hop (ARCHITECTURE.md §4.1, Docs/ADMIN_INTEGRATIONS_UI.md §2).
   */
  baseUrl: '',

  onUnauthenticated: () => {
    /*
     * Seed rather than invalidate: refetching `GET /auth/session` would answer
     * `authenticated: false` anyway, and doing it optimistically means `RequireAuth`
     * redirects on the same tick instead of after a round trip.
     */
    queryClient.setQueryData(SESSION_QUERY_KEY, ANONYMOUS_SESSION);
  },

  // M2.7, additive: hands the refusal to whoever is listening inside the tree.
  onStepUpRequired: (error) => {
    stepUpListener?.(error);
  },
});
